import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import {
  DEEP_DIVE_SECTIONS,
  EXEC_SUMMARY_SLUG,
  FULL_SUMMARY_SLUG,
  findExecSummaryChapter,
  isAppendixChapter
} from "@/zapi-frontend/shared/lib/projectChapters";
import type { TQueuedPill } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/GenerationStates/ChapterQueuedScreen";

export type TChapterGenState = "done" | "generating" | "queued";

const DEBUG_GEN_STATES = ["settingUp", "queued", "generating", "homeSkeleton"] as const;
export type TDebugGenState = (typeof DEBUG_GEN_STATES)[number];

const sectionOf = (chapter: TChapter) =>
  DEEP_DIVE_SECTIONS.find((section) => section.key === chapter.projectTemplateChapter.section);

// A summary chapter depends on nearly every other one, so listing each prereq gives an
// unreadable wall of pills — those show the section-level pills the Home screen uses.
const isSummaryChapter = (chapter: TChapter) =>
  chapter.projectTemplateChapter.isExecutiveSummary ||
  chapter.projectTemplateChapter.slug === EXEC_SUMMARY_SLUG ||
  chapter.projectTemplateChapter.slug === FULL_SUMMARY_SLUG;

function buildQueuedPills(chapter: TChapter, unfinished: TChapter[]): TQueuedPill[] {
  if (isSummaryChapter(chapter)) {
    const sections = DEEP_DIVE_SECTIONS.filter((section) =>
      unfinished.some((c) => c.projectTemplateChapter.section === section.key)
    ).map((section) => ({ key: section.key, nameKey: section.labelKey, IconComponent: section.Icon }));
    const sectionless = unfinished
      .filter((c) => !sectionOf(c))
      .map((c) => ({
        key: c.projectTemplateChapter.slug || String(c.id),
        name: c.projectTemplateChapter.name,
        icon: c.projectTemplateChapter.icon
      }));
    return [...sections, ...sectionless];
  }

  // A chapter pill carries its section's icon rather than its own.
  return unfinished.map((c) => ({
    key: c.projectTemplateChapter.slug || String(c.id),
    name: c.projectTemplateChapter.name,
    icon: c.projectTemplateChapter.icon,
    IconComponent: sectionOf(c)?.Icon
  }));
}

export function useProjectGenerationState(projectId: string) {
  const taskId = useProjectSetupStore((s) => s.activeSetupTasks[projectId] ?? null);
  const status = useProjectSetupStore((s) => s.taskStatuses[projectId] ?? null);
  const chapterStatusMap = useProjectSetupStore((s) => s.chapterStatusMaps[projectId] ?? null);
  const memoChapters = useChapterMemoStore((s) => s.chaptersByProject[projectId]);
  const chapterStoreProjectId = useChapterStore((s) => s.projectId);
  const projectChapters = useChapterStore((s) => s.projectChapters);
  const { isLabs } = useMeStore();
  const [searchParams] = useSearchParams();
  const debugParam = searchParams.get("debugGenState");

  return useMemo(() => {
    const chaptersLoaded = chapterStoreProjectId === projectId;
    const chapters = chaptersLoaded ? projectChapters : [];
    const visibleChapters = chapters.filter((c) => (!c.isHidden || isLabs) && !isAppendixChapter(c));

    // UI-only simulator of the generation states (labs users / dev builds):
    // `?debugGenState=settingUp|queued|generating|homeSkeleton`. Touches no data;
    // ProjectDetailsPage stubs out Cancel while it is set.
    const debugState =
      (isLabs || import.meta.env.DEV) && DEBUG_GEN_STATES.includes(debugParam as TDebugGenState)
        ? (debugParam as TDebugGenState)
        : null;
    if (debugState) {
      const fakeChapterState = (chapter: TChapter): TChapterGenState => {
        if (debugState === "queued") return "queued";
        if (debugState === "generating") return "generating";
        const idx = visibleChapters.indexOf(chapter);
        return idx % 3 === 0 ? "done" : idx % 3 === 1 ? "generating" : "queued";
      };
      return {
        debugState,
        isSetupActive: true,
        isSettingUp: debugState === "settingUp",
        isGenerationDataPending: false,
        isHomeGenerating: debugState === "homeSkeleton",
        visibleChapters,
        getChapterState: fakeChapterState,
        getUnfinishedPrereqs: (chapter: TChapter) =>
          visibleChapters.filter((c) => c.id !== chapter.id).slice(0, 3),
        getQueuedPills: (chapter: TChapter) =>
          buildQueuedPills(
            chapter,
            visibleChapters.filter((c) => c.id !== chapter.id).slice(0, 6)
          ),
        homeBlockerSections: [...DEEP_DIVE_SECTIONS],
        homeBlockerChapters: [] as TChapter[]
      };
    }

    const isSetupActive = !!taskId;

    const isChapterDone = (chapter: TChapter) => {
      const memoStatus = memoChapters?.find((m) => m.id === chapter.id);
      if (memoStatus) return memoStatus.hasActiveMemo;
      // The project payload is a snapshot taken before the poll answers. While a setup
      // runs it is stale — a chapter left over from an earlier run still carries an
      // activeMemo, which briefly reported Home as "generating" and flipped the page
      // through skeleton → queued right after load.
      return isSetupActive ? false : !!chapter.activeMemo;
    };

    const getChapterState = (chapter: TChapter): TChapterGenState => {
      if (isChapterDone(chapter)) return "done";
      if (chapterStatusMap?.[chapter.id.toString()]?.phase === "chapterGeneration") return "generating";
      return "queued";
    };
    const isDocumentParsing =
      !!chapterStatusMap && Object.values(chapterStatusMap).some((entry) => entry.phase === "documentParsing");
    const hasVisibleActivity = visibleChapters.some((c) => getChapterState(c) !== "queued");

    // "Setting up your Analysis" covers everything before real (visible) chapters
    // start generating: task just created, document parsing, and — for non-labs
    // users — the hidden-chapters-only stretch of the generation.
    const isSettingUp =
      isSetupActive && (status === null || status === "pending" || isDocumentParsing || !hasVisibleActivity);

    // A task exists but its data has not fully settled — either the first poll status
    // has not arrived, or this project's chapters are not in the store yet. Which
    // generation screen belongs on screen is still unknown, so picking one now means
    // swapping it a moment later — reopening a project mid-generation resolves the poll
    // before the chapters load, so "Setting up" (no visible activity yet) flashes before
    // "Home is queued". Callers render the neutral page loader until it settles.
    const isGenerationDataPending =
      isSetupActive && ((status === null && !chapterStatusMap) || !chaptersLoaded);

    const execChapter = findExecSummaryChapter(chapters);
    const isHomeGenerating = isSetupActive && !!execChapter && isChapterDone(execChapter);

    const getUnfinishedPrereqs = (chapter: TChapter): TChapter[] => {
      const slugs = chapter.projectTemplateChapter.prereqs ?? [];
      return slugs
        .map((slug) => visibleChapters.find((c) => c.projectTemplateChapter.slug === slug))
        .filter((c): c is TChapter => !!c && !isChapterDone(c));
    };

    const getQueuedPills = (chapter: TChapter) => buildQueuedPills(chapter, getUnfinishedPrereqs(chapter));

    // Home waits for the whole analysis; per design it shows section-level pills
    // instead of every unfinished chapter.
    const homeBlockerSections = DEEP_DIVE_SECTIONS.filter((section) =>
      visibleChapters.some((c) => c.projectTemplateChapter.section === section.key && !isChapterDone(c))
    );
    const templateHasSections = visibleChapters.some((c) => c.projectTemplateChapter.section);
    const homeBlockerChapters = templateHasSections ? [] : visibleChapters.filter((c) => !isChapterDone(c));

    return {
      debugState,
      isSetupActive,
      isSettingUp,
      isGenerationDataPending,
      isHomeGenerating,
      visibleChapters,
      getChapterState,
      getUnfinishedPrereqs,
      getQueuedPills,
      homeBlockerSections,
      homeBlockerChapters
    };
  }, [
    chapterStoreProjectId,
    projectId,
    projectChapters,
    isLabs,
    memoChapters,
    chapterStatusMap,
    taskId,
    status,
    debugParam
  ]);
}
